import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BotonCompartir from './Compartir';
import { imagenSorteo } from './imagenCompartir';
import { juegoDe } from './mecanicasJuegos';
import {
  useExtractosJuegos,
  pesos,
  cantidad,
  rotuloCategoria,
} from './useExtractosJuegos';
import './ExtractosJuegos.css';

/**
 * Extracto de cada juego, con el vestido de su juego.
 *
 * ── El problema, y la forma que lo resuelve ──────────────────────────────
 *
 * El tablero de la quiniela ya mostró cómo NO se hace: 25 celdas del mismo
 * peso visual, una planilla de consulta que no vende nada (N3). Acá el riesgo
 * es peor, porque hay más para mostrar: Loto Plus son cuatro modalidades con
 * seis números y hasta cuatro categorías de premio cada una. Puestas en una
 * sola tabla son 4 × 4 × 4 celdas y nadie lee eso.
 *
 * La forma la dieron los tres sitios oficiales, y las tres coinciden:
 * **la modalidad es una tarjeta, no una fila.**
 *
 *   loto.loteriadelaciudad.gob.ar   cuatro tarjetas en grilla, seis bolillas
 *                                   redondas cada una, "Sale o Sale" con
 *                                   borde de realce, y el Número PLUS en un
 *                                   bloque propio abajo. NO publica tabla de
 *                                   premios en esa pantalla.
 *   loto5.loteriadelaciudad.gob.ar  un solo bloque, bolillas redondas con
 *                                   contorno negro, con el rótulo del juego
 *                                   en una pestaña al costado.
 *   Santa Fe (Quini 6 y Brinco)     un bloque por modalidad: título, seis
 *                                   CASILLEROS CUADRADOS, y debajo una tabla
 *                                   chica cuyas columnas cambian por
 *                                   modalidad. Los repetidos del Premio Extra
 *                                   van tachados.
 *
 * De ahí salen las tres decisiones de esta pieza:
 *
 *  1. **Una tarjeta por modalidad.** Los números son el protagonista y ocupan
 *     el centro de su tarjeta, como en los tres sitios.
 *  2. **Un solo titular de premio visible por modalidad**, no la tabla. El
 *     titular es la primera categoría, que es la que vende: "Vacante · el
 *     pozo se acumula" o "3 ganadores · $19.914.147". El resto de las
 *     categorías vive en un desplegable. Así la pantalla en reposo son
 *     bolillas y una línea, y la planilla aparece solo si alguien la pide.
 *  3. **Las columnas de la tabla salen de los datos**, nunca de una lista
 *     fija: una columna sin un solo valor no se dibuja. Es lo que hace que
 *     Brinco `Extracciones` —que no publica pozo— no muestre una columna de
 *     guiones.
 *
 * ── El vestido ──────────────────────────────────────────────────────────
 * Cambia el vestido, no la casa: el marco, el ritmo, el espaciado, el pie y
 * la tipografía de la casa no se mueven. Cambian el logo, un velo del color
 * de marca, el filete, la tinta de los títulos de modalidad, la forma de la
 * bolilla —redonda, redonda con anillo o cuadrada, según la dibuje el sitio
 * del propio juego— y, donde el juego publica una, su tipografía.
 *
 * El color de marca crudo NO lleva texto nunca: vive en el velo y en el
 * filete. Para texto va `tintaAAA`, que alcanza 7:1 sobre este panel.
 */

/* ─────────────────────────── el link a un juego ───────────────────────────
   Mismo criterio que el link del tablero de la quiniela: va en palabras, se
   lee al ojo antes de tocarlo, y del otro lado abre exactamente lo que
   prometía. La app usa HashRouter, así que el parámetro va DESPUÉS del `#`.

     https://inprojuy.gob.ar/#/?juego=lotoplus

   La base sale de la ventana y no de una constante: el link tiene que abrir
   el MISMO sitio desde el que se copió. */
export function enlaceJuego(clave) {
  if (!clave) return null;
  const base =
    typeof window !== 'undefined' && window.location
      ? `${window.location.origin}${window.location.pathname}`
      : 'https://inprojuy.gob.ar/';
  return `${base}#/?juego=${encodeURIComponent(clave)}`;
}

/** Altura de la barra fija más un respiro, para no dejar el logo debajo. */
const AIRE_BARRA = 96;

/**
 * Sube hasta un nodo y SE QUEDA ahí mientras la página termina de crecer.
 *
 * Un solo `scrollTo` no alcanza y esto se vio en el preview desplegado, no en
 * teoría: quien entra por `#/?juego=lotoplus` en una carga fría queda arriba
 * de todo con el panel resaltado 2.291 px más abajo. El efecto corre apenas
 * `cargando` pasa a false, y en ese instante todo lo que hay ENCIMA del panel
 * —el hero, los logos del carrusel, el tablero de la quiniela— todavía no
 * tiene su alto final. El píxel calculado ahí deja de ser el correcto en
 * cuanto entra una imagen.
 *
 * Por eso se reafirma mientras la página se acomoda, y se corta apenas el
 * nodo queda donde tiene que estar o apenas la persona toca el scroll: si
 * decidió irse a otro lado, la página no se la pelea.
 */
function llevarA(nodo) {
  if (!nodo || typeof window === 'undefined') return undefined;

  let vivo = true;
  const soltar = () => { vivo = false; };
  const eventos = ['wheel', 'touchstart', 'keydown'];
  eventos.forEach((e) => window.addEventListener(e, soltar, { passive: true, once: true }));

  const ubicar = () => {
    if (!vivo || !nodo.isConnected) return false;
    const arriba = nodo.getBoundingClientRect().top;
    if (Math.abs(arriba - AIRE_BARRA) <= 2) return true;
    // `behavior:'instant'` NO es un detalle. La hoja global declara
    // `html{scroll-behavior:smooth}`, así que un `scrollTo` sin `behavior`
    // hereda el suave — y el suave acá se cancela: medido en el preview, un
    // `scrollTo({top:2500})` deja la página exactamente donde estaba, mientras
    // que el mismo con `instant` llega. Además, dos mil trescientos píxeles de
    // viaje animado al aterrizar no son una cortesía, son una espera.
    window.scrollTo({ top: Math.max(0, arriba + window.scrollY - AIRE_BARRA), behavior: 'instant' });
    return false;
  };

  ubicar();
  // Los reintentos cubren lo que tarda en entrar una imagen de arriba. Son
  // pocos y terminan: no es un bucle que siga corriendo mientras se navega.
  const relojes = [16, 120, 320, 700, 1200].map((ms) => window.setTimeout(() => {
    if (ubicar()) relojes.forEach(window.clearTimeout);
  }, ms));

  return () => {
    vivo = false;
    relojes.forEach(window.clearTimeout);
    eventos.forEach((e) => window.removeEventListener(e, soltar));
  };
}

/** Lee `?juego=` del hash o de la query. Devuelve la clave o null. */
export function juegoDeEnlace(hash, search) {
  const h = String(hash || '');
  const i = h.indexOf('?');
  const trozos = [String(search || ''), i === -1 ? '' : `?${h.slice(i + 1)}`];
  for (const t of trozos) {
    if (!t) continue;
    try {
      const v = new URLSearchParams(t).get('juego');
      if (v && /^[a-z0-9]{2,24}$/i.test(v)) return v.toLowerCase();
    } catch (e) {
      /* un parámetro roto no rompe la página */
    }
  }
  return null;
}

/* ───────────────────────────── el titular ─────────────────────────────── */

/**
 * La línea de premio que se ve sin desplegar nada.
 *
 * Devuelve `{ estado, detalle }` o null. Las tres situaciones se mantienen
 * separadas a propósito, porque significan cosas distintas y la fuente las
 * distingue:
 *
 *   vacante            no hubo ganador y el pozo se acumula. `VACANTE` NO es
 *                      un número: el backend lo guarda como `ganadores: 0` +
 *                      `vacante: true`, y hay que mirar `vacante` PRIMERO. Al
 *                      revés se publica "0 ganadores" donde la fuente dice
 *                      que el pozo pasa al concurso siguiente.
 *   ganadores === 0    cero real, sin marca de vacante.
 *   ganadores > 0      cuántos, y cuánto.
 *
 * El monto que se muestra es el premio por ganador cuando la fuente lo trae
 * (Santa Fe publica la columna "Premios $") y el pozo cuando no (el XML de
 * Lotería de la Ciudad no publica el unitario). El rótulo cambia con el dato:
 * llamarle "cada uno cobró" a un pozo sería decir algo que la fuente no dijo.
 */
export function titularDe(cat) {
  if (!cat) return null;

  const monto = cat.premioUnitario !== null && cat.premioUnitario !== undefined
    ? { texto: pesos(cat.premioUnitario), rotulo: 'cada uno' }
    : (cat.pozo !== null && cat.pozo !== undefined
      ? { texto: pesos(cat.pozo), rotulo: 'de pozo' }
      : null);

  /* Cuántos aciertos pagó. En "Siempre Sale" y en "Brinco Junior" el sorteo
     baja de categoría hasta que hay ganador, así que este número es medio
     titular por sí solo. Vive en la categoría y no en la modalidad, y con una
     sola categoría no hay tabla desplegable donde mostrarlo: sin esta línea
     el dato se perdía. */
  const conAciertos = (base) => (typeof cat.aciertos === 'number' && cat.aciertos > 0
    ? [base, `con ${cat.aciertos} aciertos`].filter(Boolean).join(' · ')
    : base);

  if (cat.vacante) {
    // "El pozo se acumula" no se escribe acá: lo dice la nota del reglamento
    // que la propia fuente adjunta, y decirlo por nuestra cuenta sería
    // afirmar una regla del juego que este dato no trae.
    return {
      estado: 'Vacante',
      tono: 'vacante',
      detalle: monto ? `${monto.texto} de pozo` : null,
    };
  }

  const g = typeof cat.ganadores === 'number' ? cat.ganadores : null;
  if (g === null) {
    return monto ? { estado: monto.texto, tono: 'monto', detalle: conAciertos(monto.rotulo) } : null;
  }
  if (g === 0) {
    return {
      estado: 'Sin ganadores',
      tono: 'cero',
      detalle: conAciertos(monto ? `${monto.texto} de pozo` : null),
    };
  }
  return {
    estado: `${cantidad(g)} ${g === 1 ? 'ganador' : 'ganadores'}`,
    tono: 'ganadores',
    detalle: conAciertos(monto ? `${monto.texto} ${monto.rotulo}` : null),
  };
}

/** El titular en una línea, para la imagen que se comparte. */
function titularPlano(bloque) {
  const t = titularDe(bloque.categorias[0]);
  if (!t) return null;
  const nombre = bloque.categorias[0] ? rotuloCategoria(bloque.categorias[0]) : null;
  return [nombre, t.estado, t.detalle].filter(Boolean).join(' · ');
}

/* ───────────────────────────── las piezas ─────────────────────────────── */

/**
 * Cuántas bolillas por fila.
 *
 * Con `flex-wrap` el reparto lo decidía el ancho sobrante y en móvil salía
 * 5 + 1: cinco bolillas y una sola abajo, huérfana. Se vio en la captura.
 * Acá el número de columnas es explícito y el reparto queda parejo en todos
 * los anchos.
 *
 *   5 o menos  → todas en una fila. Es como publica Loto 5 Plus sus cinco
 *                bolillas, y como se lee el Pozo de la Quiniela.
 *   6          → 3 × 2, la grilla de `loto.loteriadelaciudad.gob.ar`
 *                (`.bolillas-container{grid-template-columns:repeat(3,1fr)}`).
 *   más        → 6 por fila. Es el `col-xs-2` de Santa Fe, que reparte el
 *                Premio Extra de Quini 6 en filas de seis.
 *
 * ⚠️ Desvío consciente: Santa Fe también dibuja sus modalidades de SEIS
 * números en una sola fila de seis. Acá van 3 × 2 porque las tarjetas de
 * modalidad van de a cuatro y seis bolillas en fila darían casilleros de
 * 32 px. Se prefiere la grilla de la otra casa antes que un número ilegible.
 */
export function columnasDeBolillas(n) {
  if (n <= 5) return Math.max(1, n);
  if (n === 6) return 3;
  return 6;
}

function Bolilla({ valor, repetido, realce }) {
  return (
    <span
      className={`ej-bola${repetido ? ' ej-bola-rep' : ''}${realce ? ' ej-bola-realce' : ''}`}
    >
      {valor}
    </span>
  );
}

const SinDato = () => <span className="ej-sin" aria-label="sin dato">—</span>;

/**
 * La tabla completa de una modalidad.
 *
 * ── Por qué se dibuja APILADA y no en columnas ───────────────────────────
 * La primera versión era una tabla de cuatro columnas. En pantalla se veía
 * así: `$ 1.100.000.000` y `Vacante` **cortados por el borde de la tarjeta**.
 * La tarjeta de una modalidad mide entre 230 y 280 px porque van cuatro por
 * fila, y en ese ancho no entran cuatro columnas de las cuales tres son
 * montos de nueve dígitos. No se vio en el build: se vio en la captura.
 *
 * Se descartó el scroll horizontal: el repo ya tiene anotado como pendiente
 * que "la tabla de extractos scrollea en horizontal y nada avisa". Agregar
 * una segunda no es una solución, es el mismo problema otra vez.
 *
 * Entonces cada fila se apila: el rótulo de la categoría arriba y debajo un
 * par etiqueta/valor por dato. Entra en cualquier ancho y se lee igual en
 * móvil, sin un segundo maquetado que mantener.
 *
 * ── Sigue siendo una tabla para quien no la ve ───────────────────────────
 * `display:block` sobre `<table>`, `<tr>` y `<td>` **le saca a la tabla su
 * rol** en el árbol de accesibilidad: el lector deja de anunciar filas y
 * columnas y lo lee como texto suelto. Por eso los roles van escritos a mano
 * (`table`, `rowgroup`, `row`, `columnheader`, `rowheader`, `cell`) y la
 * cabecera se oculta a la vista pero NO al lector: cada celda muestra su
 * etiqueta con `data-rotulo`, y el lector la obtiene del `<th scope="col">`
 * que sigue estando.
 */
function TablaPremios({ bloque }) {
  const { columnas, categorias } = bloque;
  /* `jsx-a11y/no-redundant-roles` dice que el rol de `thead`/`tbody` es
     implícito y que declararlo sobra. Acá NO sobra: el CSS los pone en
     `display:block` para que la tabla entre en una tarjeta de 230 px, y eso
     le saca a los elementos de tabla su rol implícito. Sin estos roles el
     lector de pantalla deja de anunciar filas y columnas. La regla es
     correcta en general y falsa en este caso concreto. */
  /* eslint-disable jsx-a11y/no-redundant-roles */
  return (
    <table className="ej-tabla" role="table">
      <thead role="rowgroup">
        <tr role="row">
          {columnas.rotulo && <th role="columnheader" scope="col">Premio</th>}
          {columnas.pozo && <th role="columnheader" scope="col">Pozo</th>}
          {columnas.aciertos && <th role="columnheader" scope="col">Aciertos</th>}
          {columnas.ganadores && <th role="columnheader" scope="col">Ganadores</th>}
          {columnas.premioUnitario && <th role="columnheader" scope="col">Cada ganador</th>}
        </tr>
      </thead>
      <tbody role="rowgroup">
        {categorias.map((c, i) => (
          <tr role="row" key={`${c.clave}-${i}`}>
            {columnas.rotulo && (
              <th role="rowheader" scope="row">{rotuloCategoria(c)}</th>
            )}
            {columnas.pozo && (
              <td role="cell" data-rotulo="Pozo">{pesos(c.pozo) || <SinDato />}</td>
            )}
            {columnas.aciertos && (
              <td role="cell" data-rotulo="Aciertos">
                {c.aciertos !== null ? c.aciertos : <SinDato />}
              </td>
            )}
            {columnas.ganadores && (
              <td role="cell" data-rotulo="Ganadores">
                {/* `vacante` se mira antes que `ganadores`: la fuente guarda
                    los dos y el cero de al lado no es un cero de verdad. */}
                {c.vacante
                  ? <b className="ej-vacante">Vacante</b>
                  : (c.ganadores !== null ? cantidad(c.ganadores) : <SinDato />)}
              </td>
            )}
            {columnas.premioUnitario && (
              <td role="cell" data-rotulo="Cada ganador">
                {pesos(c.premioUnitario) || <SinDato />}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
  /* eslint-enable jsx-a11y/no-redundant-roles */
}

function Modalidad({ bloque, juego, idBase }) {
  const titular = titularDe(bloque.categorias[0]);
  const rotulo = bloque.categorias[0] ? rotuloCategoria(bloque.categorias[0]) : null;
  const hayTabla = bloque.categorias.length > 0;
  const ancha = bloque.numeros.length > 7;

  return (
    <section className={`ej-mod${ancha ? ' ej-mod-ancha' : ''}`} aria-labelledby={`${idBase}-t`}>
      <h5 className="ej-mod-nombre" id={`${idBase}-t`}>{bloque.nombre}</h5>

      {/* Los números son el protagonista: es lo que la gente vino a ver y lo
          único que se lee desde lejos. */}
      <ol
        className="ej-bolas"
        style={{ '--ej-cols': columnasDeBolillas(bloque.numeros.length) }}
        aria-label={`Números de ${bloque.nombre}`}
      >
        {bloque.numeros.map((n, i) => (
          <li key={`${i}-${n}`}>
            <Bolilla valor={n} repetido={bloque.repetidos[i]} />
          </li>
        ))}
      </ol>

      {bloque.destacado && (
        <p className="ej-destacado">
          <Bolilla valor={bloque.destacado.valor} realce />
          <span>{bloque.destacado.rotulo}</span>
        </p>
      )}

      {bloque.meta.length > 0 && (
        <dl className="ej-meta">
          {bloque.meta.map((m) => (
            <div key={m.rotulo}>
              <dt>{m.rotulo}</dt>
              <dd>{m.valor}</dd>
            </div>
          ))}
        </dl>
      )}

      {titular && (
        <p className={`ej-titular ej-titular-${titular.tono}`}>
          {rotulo && <span className="ej-titular-cat">{rotulo}</span>}
          <b>{titular.estado}</b>
          {titular.detalle && <span className="ej-titular-det">{titular.detalle}</span>}
        </p>
      )}

      {/* Los aciertos NO llevan línea propia: el parser del backend copia el
          `<Aciertos>` de la modalidad dentro de cada categoría, así que una
          línea acá y el "con 5 aciertos" del titular son el mismo dato dicho
          dos veces. Se vio en la captura de Sale o Sale. */}

      {/* El resto de las categorías, a un clic. En reposo la tarjeta son
          bolillas y una línea; la planilla aparece solo si alguien la pide. */}
      {hayTabla && bloque.categorias.length > 1 && (
        <details className="ej-premios">
          <summary>
            <span>Todos los premios</span>
            <span className="ej-chevron" aria-hidden="true" />
          </summary>
          <TablaPremios bloque={bloque} />
        </details>
      )}

      {bloque.notas.map((n) => (
        <p className="ej-nota" key={n}>{n}</p>
      ))}

      {bloque.desconocida && (
        // Una modalidad que el backend no reconoció se muestra igual, con su
        // nombre textual: perderla sería peor que no reconocerla.
        <p className="ej-nota">Modalidad nueva, tal como la nombra la fuente. {juego}.</p>
      )}
    </section>
  );
}

/* ─────────────────────────── el panel del juego ───────────────────────── */

function CompartirJuego({ juego }) {
  const bloques = useMemo(
    () => juego.bloques.map((b) => ({
      nombre: b.nombre,
      numeros: b.numeros,
      repetidos: b.repetidos,
      titular: titularPlano(b),
      destacado: b.destacado
        ? { ...b.destacado, color: juego.tema.realce || undefined }
        : null,
    })),
    [juego],
  );

  const contexto = [
    juego.sorteoNro ? `Sorteo Nº ${juego.sorteoNro}` : null,
    juego.fecha,
  ].filter(Boolean).join(' · ');

  const hacerArchivo = useCallback(
    () => imagenSorteo({
      juego: juego.nombre,
      logo: juego.tema.logo,
      acento: juego.tema.acento,
      tinta: juego.tema.tintaAAA,
      ficha: juego.tema.ficha,
      contexto,
      pozo: pesos(juego.pozoEstimado) || undefined,
      proximo: juego.proximo
        ? `Próximo sorteo: ${juego.proximo.cuando} ${juego.proximo.fecha}`
        : undefined,
      bloques,
      plus: juego.plus
        ? { rotulo: juego.plus.nombre, valor: juego.plus.numero, color: juego.tema.realce }
        : undefined,
      atribucion: juego.entidad
        ? `Extracto oficial publicado por ${juego.entidad}. Verificalo en`
        : 'Extracto oficial. Verificalo en',
    }),
    [juego, bloques, contexto],
  );

  const que = `del extracto de ${juego.nombre}${contexto ? `, ${contexto}` : ''}`;
  const enlace = enlaceJuego(juego.clave) || undefined;

  const texto = [
    `${juego.nombre}${contexto ? ` · ${contexto}` : ''}`,
    '',
    ...juego.bloques.map((b) => `${b.nombre}: ${b.numeros.join(' · ')}`),
    juego.plus ? `${juego.plus.nombre}: ${juego.plus.numero}` : null,
    '',
    juego.entidad ? `Extracto oficial publicado por ${juego.entidad}.` : 'Extracto oficial.',
    'Verificalo acá:',
  ].filter((l) => l !== null).join('\n');

  return (
    <BotonCompartir
      hacerArchivo={hacerArchivo}
      firma={`${juego.clave}|${juego.sorteoNro}|${juego.fechaISO}`}
      titulo={`${juego.nombre}${contexto ? ` · ${contexto}` : ''}`}
      texto={texto}
      aria={`Compartir la imagen ${que}`}
      ariaCopia={`Copiar la imagen ${que}`}
      enlace={enlace}
      resumen={`${juego.nombre}${contexto ? ` · ${contexto}` : ''}`}
      ariaEnlace={`Enviar por WhatsApp el link ${que}`}
      // El panel es oscuro: `noche` es la variante hecha para eso. Con
      // `solido` el segundo botón queda verde petróleo sobre fondo oscuro
      // y no se lee — verificado en la primera captura.
      variante="noche"
    />
  );
}

function PanelJuego({ juego, resaltado, revelado }) {
  const t = juego.tema;
  const mecanica = juegoDe(juego.clave);
  const notaPlus = mecanica && mecanica.clave === juego.clave ? mecanica.extra?.nota : null;

  const estilo = {
    '--ej-acento': t.acento,
    '--ej-tinta': t.tintaAAA,
    '--ej-realce': t.realce || t.tintaAAA,
    '--ej-fuente': t.fuente || 'var(--display)',
  };

  return (
    <article
      className={`ej-juego ej-reveal${revelado ? ' in' : ''}${resaltado ? ' ej-resaltado' : ''}`}
      style={estilo}
      data-ficha={t.ficha}
      data-juego={juego.clave}
      id={`ej-${juego.clave}`}
      aria-labelledby={`ej-${juego.clave}-nombre`}
      tabIndex={-1}
    >
      {/* El velo: la luz del juego entra por detrás del logo y muere antes del
          centro. Es la firma que ya usa el carrusel del hero, y va sin texto
          encima, que es lo que le permite usar el color de marca crudo. */}
      <span className="ej-velo" aria-hidden="true" />

      <header className="ej-cab">
        <span className={`ej-logo${t.plato ? ' ej-logo-plato' : ''}`}>
          {/* Trampa 2 del repo: width/height al 100% con object-fit, nunca
              max-height + width:auto. */}
          <img src={t.logo} alt={juego.nombre} />
        </span>

        <div className="ej-ident">
          <h4 id={`ej-${juego.clave}-nombre`}>{juego.nombre}</h4>
          <p className="ej-sorteo">
            {juego.sorteoNro ? <>Sorteo Nº <b>{juego.sorteoNro}</b></> : null}
            {juego.sorteoNro && juego.fecha ? ' · ' : null}
            {juego.fecha}
            {juego.hora ? ` · ${juego.hora}` : null}
          </p>
        </div>

        {(juego.pozoEstimado || juego.proximo) && (
          <dl className="ej-cab-datos">
            {juego.pozoEstimado && (
              <div>
                <dt>Pozo estimado</dt>
                <dd className="ej-monto">{pesos(juego.pozoEstimado)}</dd>
              </div>
            )}
            {juego.proximo && (
              <div>
                <dt>Próximo sorteo</dt>
                <dd>
                  {juego.proximo.cuando} {juego.proximo.fecha}
                  {juego.proximoHora ? ` · ${juego.proximoHora}` : ''}
                </dd>
              </div>
            )}
          </dl>
        )}
      </header>

      <span className="ej-filete" aria-hidden="true" />

      {/* El pozo dudoso se DICE, no se esconde: que el campo tenga un valor no
          alcanza, puede ser el de la corrida anterior conservado a propósito. */}
      {juego.pozoDudoso && (
        <p className="ej-aviso">
          El pozo estimado de este sorteo no se pudo confirmar, así que no se
          publica. Los números y los premios sí están verificados.
        </p>
      )}

      <div className="ej-mods" data-cant={juego.bloques.length}>
        {juego.bloques.map((b) => (
          <Modalidad
            key={b.clave}
            bloque={b}
            juego={juego.nombre}
            idBase={`ej-${juego.clave}-${b.clave}`}
          />
        ))}
      </div>

      {/* El Número PLUS va en un bloque propio, como lo dibuja el sitio de
          Loto Plus: no es una modalidad más, es una bolilla que se juega
          aparte y multiplica el premio de las otras. */}
      {juego.plus && (
        <div className="ej-plus">
          <Bolilla valor={juego.plus.numero} realce />
          <div className="ej-plus-texto">
            <strong>{juego.plus.nombre}</strong>
            {notaPlus && <span>{notaPlus}</span>}
          </div>
        </div>
      )}

      <footer className="ej-pie">
        <div className="ej-pie-datos">
          {/* La nota del reglamento que la fuente repite en cada modalidad
              vacante se dice UNA vez, acá. Repetida cinco veces pesa más que
              los números. */}
          {juego.notasComunes.map((n) => (
            <p className="ej-nota" key={n}>{n}</p>
          ))}
          {juego.autoridades.length > 0 && (
            <p>
              {juego.autoridades
                .map((a) => (a.tipo ? `${a.tipo}: ${a.nombre}` : a.nombre))
                .join(' · ')}
            </p>
          )}
          {juego.prescripcion && <p>Los premios prescriben el {juego.prescripcion}.</p>}
          {juego.entidad && (
            <p>
              Extracto oficial publicado por {juego.entidad}
              {juego.fuente ? ` · ${juego.fuente}` : ''}
            </p>
          )}
        </div>

        <div className="ej-pie-acciones">
          <CompartirJuego juego={juego} />
          {juego.extractoPdfUrl && (
            <a
              className="ej-pdf"
              href={juego.extractoPdfUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Extracto oficial en PDF <span aria-hidden="true">↗</span>
            </a>
          )}
        </div>
      </footer>
    </article>
  );
}

/* ──────────────────────────── los tres estados ────────────────────────── */

/** Esqueleto. Cero dígitos: ni un número de relleno, ni siquiera gris (N1). */
function Esqueleto() {
  return (
    <div className="ej-juego ej-cargando ej-reveal" aria-busy="true" aria-live="polite">
      <span className="hc-sr-only">Buscando los extractos de los juegos</span>
      <div className="ej-cab">
        <span className="ej-esq ej-esq-logo" />
        <div className="ej-ident">
          <span className="ej-esq ej-esq-titulo" />
          <span className="ej-esq ej-esq-linea" />
        </div>
      </div>
      <div className="ej-mods" data-cant="4">
        {[0, 1, 2, 3].map((i) => (
          <section className="ej-mod" key={i}>
            <span className="ej-esq ej-esq-rotulo" />
            <ol className="ej-bolas">
              {[0, 1, 2, 3, 4, 5].map((j) => (
                <li key={j}><span className="ej-bola ej-bola-vacia" /></li>
              ))}
            </ol>
            <span className="ej-esq ej-esq-linea" />
          </section>
        ))}
      </div>
    </div>
  );
}

export default function ExtractosJuegos({ extractos, cargandoExtractos }) {
  const { juegos, cargando, muestra } = useExtractosJuegos(extractos, cargandoExtractos);

  /* El link a un juego. Se lee UNA vez, al montar, pero resolverlo depende de
     que hayan llegado los datos: el efecto cuelga de `juegos` y no de []. Es
     la trampa 1 de este repo con otra ropa — el observador que se registra al
     montar y nunca ve lo que llegó después de Firestore. */
  const [pedido] = useState(() => (typeof window !== 'undefined'
    ? juegoDeEnlace(window.location.hash, window.location.search)
    : null));
  const [resaltado, setResaltado] = useState(null);
  /* Los paneles se muestran de una, sin esperar el scroll. Solo lo enciende el
     link a un juego; quien entra por la home conserva la entrada animada. */
  const [revelarYa, setRevelarYa] = useState(false);
  const [avisoEnlace, setAvisoEnlace] = useState(null);
  const resuelto = useRef(false);
  const cajaRef = useRef(null);

  useEffect(() => {
    if (!pedido || resuelto.current || cargando) return;
    resuelto.current = true;

    const encontrado = juegos.find((j) => j.clave === pedido);
    if (!encontrado) {
      setAvisoEnlace(pedido);
      return;
    }
    setResaltado(pedido);
    const nodo = document.getElementById(`ej-${pedido}`);
    if (nodo) {
      // El reveal es un premio por scrollear, y quien llega por el link no
      // scrolleó: lo teletransportamos. Sin esto aterriza sobre su panel en
      // `opacity:0` —medido: cae en el píxel correcto y no ve nada— esperando
      // una entrada que ya se salteó.
      //
      // Va por estado y NO por `classList.add`: la primera versión escribía la
      // clase en el DOM y el panel resaltado se quedaba igual invisible, porque
      // `setResaltado` de la línea de arriba lo vuelve a renderizar y React
      // reescribe `className` completo, pisando la clase agregada a mano. Los
      // otros paneles, que no cambiaban de props, sí la conservaban. Se veían
      // dos de tres: justo el que pidió el link era el que faltaba.
      setRevelarYa(true);
      llevarA(nodo);
      nodo.focus?.({ preventScroll: true });
    }
    return undefined;
  }, [pedido, juegos, cargando]);

  useEffect(() => {
    if (!avisoEnlace || !cajaRef.current) return undefined;
    return llevarA(cajaRef.current);
  }, [avisoEnlace]);

  /* ── El reveal es PROPIO, no el de la home ──────────────────────────────
     `HomeComercial` registra su `IntersectionObserver` sobre `.hc-reveal` con
     dependencia `[extractos]`. Alcanza para el tablero de la quiniela, que
     aparece justo cuando llega ese dato, y NO alcanza para esta sección:

      · el aviso de "sin datos" aparece cuando `cargando` pasa a false con
        `extractos` todavía en null — el efecto de la home nunca se vuelve a
        correr y el aviso se queda en `opacity:0`. **Pasó**: la captura del
        estado sin datos salió en blanco.
      · el día que se encienda `JUEGOS_CON_EXTRACTO`, los paneles van a
        aparecer cuando contesten `sorteos_estado` y `sorteos_juegos`, que
        tampoco tocan `extractos`. Sería el mismo bug, ya desplegado.

     Es la trampa 1 del repo por tercera vez. La cura no es agregar otra
     dependencia al efecto ajeno: es que esta sección observe sus propios
     nodos, con las dependencias que ella sí conoce. Por eso la clase es
     `ej-reveal` y no `hc-reveal`. */
  useEffect(() => {
    const caja = cajaRef.current;
    if (!caja) return undefined;
    const nodos = caja.querySelectorAll('.ej-reveal:not(.in)');
    if (!nodos.length) return undefined;

    const reducido = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducido || !('IntersectionObserver' in window)) {
      nodos.forEach((n) => n.classList.add('in'));
      return undefined;
    }

    const io = new IntersectionObserver((entradas) => {
      entradas.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add('in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.08 });
    nodos.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [juegos, cargando, avisoEnlace, muestra]);

  /* El bloque se dibuja SIEMPRE, en sus tres estados. No se esconde cuando no
     hay datos: "todavía no hay extracto publicado de estos juegos" es una
     respuesta, y esconder la sección deja al visitante sin saber si el sitio
     no los cubre o si todavía no salieron. Es un mensaje distinto del que da
     el tablero de arriba, que habla de la quiniela del día. */

  /* ⚠️ El `ej-reveal` va en la CABECERA y en CADA PANEL, nunca en este
     envoltorio. El ratio de intersección de un elemento más alto que la
     ventana está topeado por `altoVentana / altoElemento`. En móvil este
     envoltorio mide ~6.000 px contra una ventana de 844: el techo es
     776/6000 = 12,9 %, y con un juego más se va abajo del umbral — la sección
     entera se queda en `opacity:0` para siempre.
     No es hipotético: la primera corrida de capturas falló justo ahí, con
     "#ej-lotoplus sigue invisible" en móvil. Es la misma trampa 1 por otro
     camino: no "el nodo llegó tarde al observer" sino "el nodo nunca alcanza
     el umbral". Un panel suelto mide como mucho ~2.500 px y da 31 %. */

  return (
    <div className="ej" ref={cajaRef}>
      <div className="ej-head ej-reveal">
        <div>
          {/* El título NO enumera los juegos. Enumerarlos —"Loto, Quini,
              Brinco y el Pozo"— promete cuatro cuando hoy hay uno publicado,
              y volvería a mentir cada vez que una fuente falle. Dice lo que
              la sección hace, no lo que espera tener. */}
          <p className="ej-kicker">Además de la quiniela</p>
          <h3>El extracto de cada juego</h3>
        </div>
        <p>
          Las modalidades, los números y los premios de cada sorteo, tal como
          los publica la entidad que lo corre.
        </p>
      </div>

      {muestra && (
        /* Tercer candado del modo muestra. Sin esta banda, una captura se
           podría confundir con un extracto publicado — que es el modo de
           falla exacto que N1 existe para evitar. */
        <p className="ej-banda-muestra" role="status">
          <b>Datos de muestra.</b> Estos extractos no salen de Firestore: son
          los archivos oficiales de las entidades convertidos a mano para
          desarrollo. No es lo que ve un visitante.
        </p>
      )}

      {avisoEnlace && (
        <p className="ej-aviso ej-aviso-enlace" role="status">
          No hay un extracto publicado de <strong>{avisoEnlace}</strong>. Estos
          son los que sí.
        </p>
      )}

      {cargando ? (
        <Esqueleto />
      ) : juegos.length ? (
        juegos.map((j) => (
          <PanelJuego
            key={j.clave}
            juego={j}
            resaltado={resaltado === j.clave}
            revelado={revelarYa}
          />
        ))
      ) : (
        <div className="ej-juego ej-sindatos ej-reveal">
          <p>Todavía no hay extractos publicados de estos juegos.</p>
          <p>
            Los resultados oficiales están en loterialajujeña.com.ar,
            loto.loteriadelaciudad.gob.ar y loteriasantafe.gov.ar
          </p>
        </div>
      )}
    </div>
  );
}
